import { usePostHog } from 'posthog-js/react';
import { useCallback, useEffect, useState } from 'react';
import { Wizard } from 'react-use-wizard';
import type { Survey, SurveyQuestionType } from 'posthog-js';
import type { ReactElement } from 'react';

import { ProgressBar } from './ProgressBar';
import { defaultQuestionRenderers } from './questions/registry';
import { QuestionStep } from './QuestionStep';
import type { QuestionRenderer } from './questions/registry';

import styles from './SurveyWizard.module.less';

type Answer = string | string[];

const EMPTY_STEPS: ReactElement[] = [];

type Props = {
  /** PostHog survey data — typically returned from `posthog.getActiveMatchingSurveys`. */
  survey: Survey;
  /** Steps to render before the question steps (e.g. a welcome screen). */
  preSteps?: ReactElement[];
  /** Steps to render after the question steps (e.g. a thanks screen). */
  postSteps?: ReactElement[];
  /**
   * Map of `SurveyQuestionType` to the component used to render that type.
   * Defaults to `defaultQuestionRenderers` (single + multiple choice). Spread
   * to extend with additional types: `{ ...defaultQuestionRenderers, [Open]: ... }`.
   */
  questionRenderers?: Partial<Record<SurveyQuestionType, QuestionRenderer>>;
  /**
   * Called after `posthog.capture('survey sent', ...)` fires (i.e. when the
   * user advances past the last question, before the post-step renders). Lets
   * callers attach survey-specific completion side effects without coupling
   * them to this generic component — e.g. setting a person property to
   * suppress the survey on future page loads.
   */
  onSubmit?: () => void;
  /** Whether to render the built-in progress bar above the steps. Defaults to false. */
  shouldShowProgressBar?: boolean;
  /**
   * Forwarded to each `QuestionStep`. `'default'` renders the standard
   * Back/Next action row on a gray footer band. `'compact'` renders a single
   * full-width primary submit button with no Back affordance — appropriate for
   * popup-style surveys (NPS) that fit in tight corners. Defaults to `'default'`.
   */
  variant?: 'default' | 'compact';
};

const answerKey = (q: Survey['questions'][number], i: number) => q.id ?? `idx-${i}`;

/**
 * Renders a PostHog survey as a step-by-step wizard built on `react-use-wizard`.
 *
 * Owns the answer state (keyed by question id, falling back to index), fires
 * `posthog.capture('survey shown')` once on mount, and `posthog.capture('survey
 * sent', { $survey_id, $survey_response_<id>: ... })` when the user advances
 * past the last question. Pre/post steps are caller-provided so the wizard
 * primitive stays survey-agnostic — onboarding surveys plug in a welcome card
 * and a thanks card; other surveys can plug in their own surrounding elements.
 */
export const SurveyWizard = ({
  survey,
  preSteps = EMPTY_STEPS,
  postSteps = EMPTY_STEPS,
  questionRenderers = defaultQuestionRenderers,
  onSubmit,
  shouldShowProgressBar = false,
  variant = 'default',
}: Props) => {
  const posthog = usePostHog();
  const [answers, setAnswers] = useState<Record<string, Answer>>({});

  useEffect(() => {
    posthog?.capture('survey shown', { $survey_id: survey.id });
  }, [posthog, survey.id]);

  const submitToPostHog = useCallback(() => {
    if (!posthog) {
      return;
    }
    const properties: Record<string, unknown> = { $survey_id: survey.id };
    survey.questions.forEach((q, i) => {
      const key = answerKey(q, i);
      properties[`$survey_response_${key}`] = answers[key];
    });
    posthog.capture('survey sent', properties);
    onSubmit?.();
  }, [posthog, survey.id, survey.questions, answers, onSubmit]);

  const setAnswer = useCallback((key: string, value: Answer) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
  }, []);

  const lastIndex = survey.questions.length - 1;

  return (
    <div className={styles.wizard}>
      <Wizard header={shouldShowProgressBar ? <ProgressBar /> : undefined}>
        {preSteps}
        {survey.questions.map((q, i) => {
          const key = answerKey(q, i);
          return (
            <QuestionStep
              key={key}
              question={q}
              value={answers[key]}
              onChange={(v) => setAnswer(key, v)}
              onSubmit={i === lastIndex ? submitToPostHog : undefined}
              renderers={questionRenderers}
              variant={variant}
            />
          );
        })}
        {postSteps}
      </Wizard>
    </div>
  );
};
